import api from './api';

const mfaService = {
  // Get MFA status for current user
  getMFAStatus: async () => {
    try {
      const response = await api.get('/api/mfa/status/');
      return response.data;
    } catch (error) {
      console.error('Error fetching MFA status:', error);
      throw error;
    }
  },

  // Get study MFA settings
  getStudyMFASettings: async (studyId) => {
    try {
      const response = await api.get(`/api/studies/${studyId}/mfa/settings/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching study MFA settings:', error);
      throw error;
    }
  },

  // Update study MFA settings
  updateStudyMFASettings: async (studyId, settings) => {
    try {
      const response = await api.post(`/api/studies/${studyId}/mfa/settings/`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating study MFA settings:', error);
      throw error;
    }
  },

  // Override MFA requirement for a specific user in a study
  overrideUserMFA: async (studyId, userId, overrideData) => {
    try {
      const response = await api.post(`/api/studies/${studyId}/mfa/users/${userId}/`, overrideData);
      return response.data;
    } catch (error) {
      console.error('Error overriding user MFA:', error);
      throw error;
    }
  },

  // Setup MFA for current user
  setupMFA: async (method = 'totp', deviceName = null) => {
    try {
      console.log('[MFA SERVICE] Setting up MFA with method:', method, 'deviceName:', deviceName);
      
      // Use provided device name or generate a fallback
      const finalDeviceName = deviceName || `Browser_${Date.now()}`;
      
      const payload = {
        method,
        device_name: finalDeviceName
      };
      
      console.log('[MFA SERVICE] Using device name:', finalDeviceName);
      
      console.log('[MFA SERVICE] Sending payload:', payload);
      const response = await api.post('/api/mfa/setup/', payload);
      console.log('[MFA SERVICE] Setup response:', response.data);
      return response.data;
    } catch (error) {
      console.error('[MFA SERVICE] Error setting up MFA:', error);
      console.error('[MFA SERVICE] Error response:', error.response?.data);
      throw error;
    }
  },

  // Verify MFA setup
  verifyMFASetup: async (code, method = 'totp') => {
    try {
      console.log('[MFA SERVICE] Verifying MFA setup with code:', code.substring(0, 3) + '***');
      const response = await api.post('/api/mfa/verify/', { code, method });
      console.log('[MFA SERVICE] MFA verification response:', response.data);
      return response.data;
    } catch (error) {
      console.error('[MFA SERVICE] Error verifying MFA setup:', error);
      console.error('[MFA SERVICE] Error response:', error.response?.data);
      throw error;
    }
  },

  // Verify MFA code during login
  verifyMFA: async (code, method = 'totp', rememberDevice = false) => {
    try {
      // DEBUG: Check token availability before MFA verification
      const authToken = localStorage.getItem('authToken');
      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      console.log('[MFA SERVICE] Token debug before verification:', {
        hasAuthToken: !!authToken,
        authTokenLength: authToken ? authToken.length : 0,
        hasRefreshToken: !!refreshToken,
        refreshTokenLength: refreshToken ? refreshToken.length : 0
      });
      
      if (!authToken) {
        console.error('[MFA SERVICE] 🚨 NO AUTH TOKEN available for MFA verification!');
      }
      
      const response = await api.post('/api/mfa/verify/', { 
        code, 
        method,
        remember_device: rememberDevice
      }, { withCredentials: true });
      return response.data;
    } catch (error) {
      console.error('Error verifying MFA:', error);
      throw error;
    }
  },

  // Disable MFA for current user
  disableMFA: async () => {
    try {
      const response = await api.post('/api/mfa/disable/');
      return response.data;
    } catch (error) {
      console.error('Error disabling MFA:', error);
      throw error;
    }
  },

  // Get trusted devices
  getTrustedDevices: async () => {
    try {
      const response = await api.get('/api/mfa/trusted-devices/');
      return response.data;
    } catch (error) {
      console.error('Error fetching trusted devices:', error);
      throw error;
    }
  },

  // Remove trusted device
  removeTrustedDevice: async (deviceId) => {
    try {
      const response = await api.delete(`/api/mfa/trusted-devices/${deviceId}/`);
      return response.data;
    } catch (error) {
      console.error('Error removing trusted device:', error);
      throw error;
    }
  },

  // Remove MFA method
  removeMFAMethod: async (methodId, password) => {
    try {
      console.log('[MFA SERVICE] Removing MFA method:', methodId);
      const response = await api.delete(`/api/mfa/methods/${methodId}/`, {
        data: { password }
      });
      console.log('[MFA SERVICE] MFA method removed successfully');
      return response.data;
    } catch (error) {
      console.error('[MFA SERVICE] Error removing MFA method:', error);
      throw error;
    }
  },

  // Resend email verification code
  resendEmailCode: async () => {
    try {
      console.log('[MFA SERVICE] Resending email verification code');
      const response = await api.post('/api/mfa/email-resend/');
      console.log('[MFA SERVICE] Email resend response:', response.data);
      return response.data;
    } catch (error) {
      console.error('[MFA SERVICE] Error resending email code:', error);
      throw error;
    }
  }
};

export default mfaService;
